<template>
  <DialogRoot v-model:open="open">
    <DialogTrigger>
      <Box
        class="flex h-10 w-10 shrink-0 items-center justify-center"
        rounded="full"
        border="lg"
      >
        <IconsAdd class="h-3.5 w-3.5" />
      </Box>
    </DialogTrigger>
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/60 py-2 data-[state=open]:animate-overlay-show"
      >
        <DialogContent
          as-child
          :aria-describedby="undefined"
          @pointer-down-outside="
            (event) => {
              const originalEvent = event.detail.originalEvent;
              const target = originalEvent.target as HTMLElement;
              if (
                originalEvent.offsetX > target.clientWidth ||
                originalEvent.offsetY > target.clientHeight
              ) {
                event.preventDefault();
              }
            }
          "
        >
          <Box
            rounded="lg"
            class="w-[90vw] max-w-[570px] bg-white focus:outline-none data-[state=open]:animate-content-show"
          >
            <div class="flex items-start justify-between gap-2 p-5">
              <DialogTitle
                class="font-Kontesa text-[28px] font-semibold leading-[33px]"
              >
                Donate more
              </DialogTitle>
              <Box class="h-8 w-8 shrink-0 bg-white" rounded="full">
                <DialogClose
                  class="flex h-full w-full items-center justify-center"
                >
                  <IconsClose class="h-3.5 w-3.5 stroke-[4]" />
                </DialogClose>
              </Box>
            </div>

            <div class="-m-px rounded-xl border border-navy bg-pink-low p-5">
              <ProfileDonateMoreStep1
                v-if="step === 1"
                :default-campaign="defaultCampaign"
                :default-values="donation"
                @next="
                  donation = $event;
                  step = 2;
                "
              />

              <ProfileDonateMoreStep2
                v-else-if="step === 2"
                :donation="donation"
                @back="step = 1"
                @success="step = 3"
              />

              <ProfileDonateMoreStep3
                v-if="step === 3"
                :donation="donation"
                @continue="open = false"
              />
            </div>
          </Box>
        </DialogContent>
      </DialogOverlay>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import type { Campaign } from "~/lib/campaigns";

export type Donation = {
  campaignId: Campaign;
  type: "subscription" | "one-time";
  amount: number;
  tip: number;
};

const props = defineProps<{
  defaultCampaign: Campaign;
  triggerOpen?: boolean;
}>();
const emit = defineEmits<{ (e: "donate"): void }>();

const open = ref(false);
const step = ref(1);

watch(
  () => props.triggerOpen,
  (v) => {
    if (v) {
      open.value = true;
      navigateTo({ query: { "donate-more": undefined } }, { replace: true });
    }
  },
);

const defaultCampaign = toRef(props, "defaultCampaign");
const defaultDonation = computed<Donation>(() => ({
  campaignId: defaultCampaign.value,
  type: "subscription",
  amount: 4,
  tip: 0.5,
}));

const donation = ref<Donation>(defaultDonation.value);

watchEffect(() => {
  if (open.value) {
    step.value = 1;
  }
});

watch(open, (open) => {
  if (open) {
    step.value = 1;
  } else {
    donation.value = defaultDonation.value;
    if (step.value === 3) emit("donate");
  }
});
</script>
